import moment from 'moment';
import { NOTIFICATION_STATUS_CODE } from '../../../enums/notification-status-code.enum';
import { latestTaskForCode, taskToStatusRecord } from '../notification-collection.converters';
import { NotificationState, NotificationStatusAdded } from '../notification-collection.state';
import { NotificationIdState } from '../../utilities/notification-id.type';
import { NotificationStateUtilities } from '../../utilities/notification-state-utlities';
import {
    backendStatusCodeForEntry,
    effectiveStatusForEntry,
    firstInProgressByPriority,
    isCancelledStatusCode,
    isConfirmedOrCancellationConfirmedCode,
    isInProgressCode,
    isInProgressOrToBeSignedCode,
    pausedIndicatorForEntry,
} from '../selectors/notification.selector';

/** What reconciling a freshly loaded collection produced. */
export type LoadReconciliation = Readonly<{
    entries: NotificationState[];
    /**
     * The earliest travel the reconciliation saw, as an ISO timestamp, or `undefined` when it saw
     * none. The timesheet's start of work is pulled back to it — see
     * `TimesheetEffects.pullBackStartOfWork$`, which is where that write belongs.
     */
    earliestTravel: string | undefined;
    /** The statuses appended, so the load can log them the way a dispatched status write is logged. */
    addedStatuses: NotificationStatusAdded[];
}>;

/** A paused-flag change the pause invariant decided on. */
export type PausedChange = Readonly<{ id: NotificationIdState; paused: boolean }>;

/**
 * Reconciles what the device recorded about each notification with what the backend just sent:
 * every status the `taskSet` reports is appended (highest `TaskNumber` per code wins), and a
 * cancellation is recorded (AVRF while the work is under way, AVR1 otherwise) when the backend
 * cancelled a notification the device still thinks is open.
 *
 * An entry whose confirmation is already through is left alone — nothing the backend sends about it
 * can change what was signed off.
 *
 * The earliest travel it comes across travels back out rather than being acted on: pulling the
 * timesheet's start of work back to it is the timesheet slice's business, and a reducer has no
 * other slice to write to. The same goes for the appended statuses, which the load logs.
 *
 * Pure, so that the load reducer can own it: the entries carry everything it reads, and `isAppStart`
 * comes in with the load rather than being looked up.
 *
 * @param entries the freshly loaded entries, with the recorded status already carried over
 * @param isAppStart whether this load is the one performed during app start
 */
export function reconcileLoadedStatuses(entries: NotificationState[], isAppStart: boolean): LoadReconciliation {
    let earliestTravel: moment.Moment | undefined;
    const addedStatuses: NotificationStatusAdded[] = [];

    const reconciled = entries.map((entry) => {
        if (isConfirmedOrCancellationConfirmedCode(effectiveStatusForEntry(entry).statusCode)) {
            return entry;
        }
        const backendStatusCode = backendStatusCodeForEntry(entry);
        const taskCodes = new Set(entry.tasks.map((task) => task.statusCode));
        let reconciledEntry = entry;

        taskCodes.forEach((taskCode) => {
            const task = latestTaskForCode(reconciledEntry.tasks, taskCode);
            if (!task) {
                return;
            }
            const status = taskToStatusRecord(task);
            if (!reconciledEntry.status.some((existing) => existing.statusCode === status.statusCode)) {
                addedStatuses.push({ id: reconciledEntry.notificationId, status });
                reconciledEntry = { ...reconciledEntry, status: [...reconciledEntry.status, status] };
            }

            if (!isAppStart && backendStatusCode === effectiveStatusForEntry(reconciledEntry).statusCode) {
                return;
            }

            if (
                taskCode !== backendStatusCode &&
                isCancelledStatusCode(backendStatusCode) &&
                !reconciledEntry.cancelled
            ) {
                reconciledEntry = {
                    ...reconciledEntry,
                    cancelled: isInProgressOrToBeSignedCode(status.statusCode)
                        ? NOTIFICATION_STATUS_CODE.AVRF
                        : NOTIFICATION_STATUS_CODE.AVR1,
                };
            }

            const startTime = moment(status.startTime);
            if (
                status.statusCode === NOTIFICATION_STATUS_CODE.TRAV &&
                (earliestTravel === undefined || startTime.isBefore(earliestTravel))
            ) {
                earliestTravel = startTime;
            }
        });

        return reconciledEntry;
    });

    return { entries: reconciled, earliestTravel: earliestTravel?.toJSON(), addedStatuses };
}

/**
 * Enforces the pause invariant: exactly one in-progress notification may be unpaused, and nothing
 * stays paused once none is in progress at all. Reports the flags to change rather than changing
 * anything, so that the load reducer can apply them to the collection it is building while
 * `handlePausedNotifications$` writes them to the entries already in the store.
 *
 * `allowNotificationPause` is a parameter rather than a config lookup, so this stays usable from a
 * reducer — the load carries the value in with it.
 *
 * @param entries the entries to enforce the invariant over
 * @param allowNotificationPause the `AllowNotificationPause` config value
 */
export function pausedChangesFor(
    entries: readonly NotificationState[],
    allowNotificationPause: boolean,
): PausedChange[] {
    if (!allowNotificationPause) {
        return [];
    }
    const totalPaused = entries.filter(pausedIndicatorForEntry);
    const inProgress = entries.filter((entry) => isInProgressCode(effectiveStatusForEntry(entry).statusCode));

    if (inProgress.length === 0) {
        return totalPaused.map((entry) => ({ id: entry.notificationId, paused: false }));
    }

    const unpaused = inProgress.filter((entry) => !pausedIndicatorForEntry(entry));
    if (unpaused.length === 0) {
        // `firstInProgressByPriority` ranks exactly the codes `isInProgressCode` accepts, so a
        // non-empty in-progress list always has a first — the fallback is unreachable in practice
        // and returning no changes is the one answer a reducer may give.
        const toStart = firstInProgressByPriority(inProgress);
        return toStart ? [{ id: toStart.notificationId, paused: false }] : [];
    }
    if (unpaused.length > 1) {
        const first = firstInProgressByPriority(unpaused);
        if (first) {
            return unpaused
                .filter((entry) => !NotificationStateUtilities.compare(entry.notificationId, first.notificationId))
                .map((entry) => ({ id: entry.notificationId, paused: true }));
        }
    }
    return [];
}

/**
 * The entries with the decided paused flags applied.
 *
 * @param entries
 * @param changes
 */
export function applyPausedChanges(
    entries: NotificationState[],
    changes: readonly PausedChange[],
): NotificationState[] {
    if (changes.length === 0) {
        return entries;
    }
    return entries.map((entry) => {
        const change = changes.find((candidate) =>
            NotificationStateUtilities.compare(candidate.id, entry.notificationId),
        );
        return change ? { ...entry, paused: change.paused } : entry;
    });
}
